import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

interface MarkdownProps {
  source: string;
}

/**
 * Rewrites in-guide links so guide-relative `*.md` references navigate
 * via React Router. Anything else (http, anchors) passes through.
 */
function rewriteHref(href: string | undefined): string {
  if (!href) return '#';
  if (href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return href;
  // numbered guide links from upstream: "01-what-is-dreamgraph.md", etc.
  const m = href.match(/^(?:\.\/)?(\d{2})-([a-z0-9-]+)\.md(#.*)?$/);
  if (m) {
    const slug = m[2];
    const hash = m[3] ?? '';
    return `/guide/${slug}${hash}`;
  }
  if (href === 'README.md' || href === './README.md') return '/guide/overview';
  if (href === 'architect-for-dummies.md' || href === './architect-for-dummies.md') return '/guide/architect-for-dummies';
  return href;
}

export function Markdown({ source }: MarkdownProps) {
  return (
    <div className="prose">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug, [rehypeAutolinkHeadings, { behavior: 'wrap' }]]}
        components={{
          a({ href, children, ...props }) {
            const target = rewriteHref(href);
            const isExternal = target.startsWith('http');
            if (isExternal) {
              return (
                <a href={target} target="_blank" rel="noreferrer noopener" {...props}>
                  {children as ReactNode}
                </a>
              );
            }
            if (target.startsWith('/docs/')) {
              return <a href={target}>{children as ReactNode}</a>;
            }
            if (target.startsWith('/')) {
              return <Link to={target}>{children as ReactNode}</Link>;
            }
            return <a href={target} {...props}>{children as ReactNode}</a>;
          },
          img({ src, alt }) {
            // Map a few well-known upstream asset paths to local placeholders.
            let resolved = src ?? '';
            if (resolved.startsWith('../assets/')) {
              resolved = '/images/' + resolved.replace('../assets/', '');
            }
            if (resolved.startsWith('assets/')) {
              resolved = '/images/' + resolved.replace('assets/', '');
            }
            return <img src={resolved} alt={alt ?? ''} loading="lazy" />;
          },
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
